/**
 * Public org landing page at /p/:slug — lists only published+public_enabled forms.
 * No auth required.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FilesIcon, ArrowRight, Buildings } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicOrgLanding() {
    const { slug } = useParams();
    const [state, setState] = useState({ loading: true, data: null, error: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${BASE}/api/public/orgs/${slug}`);
                if (!cancelled) setState({ loading: false, data, error: null });
            } catch (e) {
                if (!cancelled) setState({ loading: false, data: null, error: e?.response?.data?.detail || "Organization not found" });
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (state.loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Loading…</div>
            </div>
        );
    }
    if (state.error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 auth-grid-bg">
                <div className="brut-section p-12 max-w-md text-center" data-testid="public-org-notfound">
                    <Buildings size={32} weight="duotone" className="mx-auto mb-4 text-destructive" />
                    <div className="label-overline mb-2">404</div>
                    <h1 className="text-2xl font-bold tracking-tight mb-2">Not found</h1>
                    <p className="text-sm text-muted-foreground mb-6">{state.error}</p>
                    <Link to="/"><Button variant="outline" className="rounded-sm">Back to FormFlo</Button></Link>
                </div>
            </div>
        );
    }

    const { org, forms } = state.data;
    return (
        <div className="min-h-screen bg-background" data-testid="public-org-landing">
            <header className="border-b border-border surface">
                <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
                    <Link to="/" className="font-display text-xl font-bold tracking-tight">
                        FormFlo<span className="text-primary">.</span>
                    </Link>
                    <span className="text-xs font-mono text-muted-foreground">/p/{org.slug}</span>
                </div>
            </header>
            <section className="max-w-5xl mx-auto px-6 py-12">
                <div className="label-overline mb-3">Public forms</div>
                <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-3" data-testid="public-org-name">{org.name}</h1>
                {org.description && <p className="text-muted-foreground mb-10 max-w-2xl">{org.description}</p>}
                {forms.length === 0 ? (
                    <div className="brut-section p-12 text-center" data-testid="public-no-forms">
                        <FilesIcon size={28} weight="duotone" className="mx-auto mb-4 text-muted-foreground" />
                        <h2 className="text-xl font-semibold mb-2">No public forms yet</h2>
                        <p className="text-sm text-muted-foreground">This organization hasn't published any forms publicly.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {forms.map((f) => (
                            <Link
                                key={f.id}
                                to={`/p/${slug}/${f.form_code}`}
                                className="brut-card p-6 group"
                                data-testid={`public-form-tile-${f.form_code}`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="border border-foreground p-2 rounded-sm bg-foreground text-background">
                                        <FilesIcon size={20} weight="duotone" />
                                    </div>
                                    <Badge variant="outline" className="rounded-sm font-mono text-[10px] capitalize">{f.layout_type || "single"}</Badge>
                                </div>
                                <h3 className="text-lg font-bold tracking-tight mb-1">{f.form_name}</h3>
                                <div className="text-xs font-mono text-muted-foreground mb-3">{f.form_code}</div>
                                {f.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{f.description}</p>}
                                <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono pt-3 border-t border-border">
                                    <span>OPEN FORM</span>
                                    <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
            <footer className="border-t border-border surface mt-16">
                <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-muted-foreground font-mono flex items-center justify-between">
                    <span>Powered by FormFlo</span>
                    <Link to="/" className="hover:text-foreground">formflo</Link>
                </div>
            </footer>
        </div>
    );
}
